/**
 * screens/admin/adminApplications.tsx — Applications catalog CRUD.
 * Faithful RN port of design/admin.jsx ApplicationsPage + ApplicationEditor.
 * NOTE: backend routes for admin Applications CRUD are a flagged 00-vision gap
 * (see api-endpoints). Operates on the working copy; service stubs exist.
 */

import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { SettingsHeader, Tag, StatusBadge } from '../../components/platform/primitives';
import { PButton, PTextInput, PTextarea, PSelect, PEmpty } from '../../components/platform/controls';
import { SideDrawer } from '../../components/platform/primitives';
import Icon, { IconName } from '../../components/icon';
import { AppIcon, ConfirmModal } from './adminShared';
import { PermissionDenied } from '../shell/chrome';
import { FS, FW, R } from '../../utils/platformTokens';

const APP_ICONS: IconName[] = ['users', 'star', 'phone', 'mail', 'eye', 'calendar', 'lock', 'home', 'settings', 'heart'];

type App = typeof import('../../data/platformSeed').SEED.applications[number];

const ApplicationEditor: React.FC<{ open: boolean; onClose: () => void; app: App | null }> = ({ open, onClose, app }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { setState } = usePlatform();
  const toast = useToast();
  const isNew = !app;
  const blank: any = { name: '', key: '', icon: 'star', category: 'Add-on', status: 'beta', desc: '', version: '0.1.0', tenants: 0 };
  const [form, setForm] = useState<any>(blank);
  const [err, setErr] = useState<{ name?: string; key?: string }>({});
  const [keyTouched, setKeyTouched] = useState(false);
  useEffect(() => { if (open) { setForm(app ? { ...app } : blank); setErr({}); setKeyTouched(false); } }, [open, app]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const save = () => {
    const e: typeof err = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!/^[a-z][a-z0-9_]*$/.test(form.key)) e.key = 'Lowercase letters, numbers & underscores; must start with a letter.';
    setErr(e);
    if (Object.keys(e).length) return;
    setState((s) => ({
      ...s,
      applications: isNew ? [...s.applications, { ...form, id: 'app_' + form.key }] : s.applications.map((a) => (a.id === app!.id ? { ...form } : a)),
    }));
    toast.success(isNew ? `${form.name} added to catalog.` : `${form.name} updated.`);
    onClose();
  };

  return (
    <SideDrawer open={open} onClose={onClose} title={isNew ? 'Add application' : `Edit ${app?.name}`}
      footer={<><PButton kind="ghost" onPress={onClose}>Cancel</PButton><PButton kind="accent" onPress={save}>{isNew ? 'Add application' : 'Save changes'}</PButton></>}>
      <View style={{ gap: 16 }}>
        <PTextInput label="Display name" placeholder="SK Analytics" value={form.name}
          onChangeText={(v) => { set('name', v); if (isNew && !keyTouched) set('key', v.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')); }}
          error={err.name} />
        <PTextInput label="App key" hint="Unique identifier used in APIs & billing." mono placeholder="analytics"
          value={form.key} onChangeText={(v) => { set('key', v); setKeyTouched(true); }} error={err.key} editable={isNew} />
        <PTextarea label="Description" rows={2} value={form.desc} onChangeText={(v) => set('desc', v)} placeholder="What does this application do?" />
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}>
            <PSelect label="Category" value={form.category} onChange={(v) => set('category', v)}
              options={[{ value: 'Core', label: 'Core' }, { value: 'Add-on', label: 'Add-on' }, { value: 'Internal', label: 'Internal' }]} />
          </View>
          <View style={{ flex: 1 }}>
            <PSelect label="Status" value={form.status} onChange={(v) => set('status', v)}
              options={[{ value: 'active', label: 'Active' }, { value: 'beta', label: 'Beta' }, { value: 'deprecated', label: 'Deprecated' }]} />
          </View>
        </View>
        <PTextInput label="Version" mono value={form.version} onChangeText={(v) => set('version', v)} />
        <View>
          <Text style={{ fontSize: FS.sm, fontWeight: FW.medium, color: c.ink2, marginBottom: 8 }}>Icon</Text>
          <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
            {APP_ICONS.map((ic) => (
              <Pressable key={ic} onPress={() => set('icon', ic)}
                style={{ width: 40, height: 40, borderRadius: R.r2, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: form.icon === ic ? c.accent : c.line, backgroundColor: form.icon === ic ? c.accentSoft : c.card }}>
                <Icon name={ic} size={18} color={form.icon === ic ? c.accent : c.ink3} />
              </Pressable>
            ))}
          </View>
        </View>
      </View>
    </SideDrawer>
  );
};

const AdminApplications: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState, isPlatformAdmin } = usePlatform();
  const toast = useToast();
  const [editor, setEditor] = useState<{ open: boolean; app: App | null }>({ open: false, app: null });
  const [confirm, setConfirm] = useState<App | null>(null);
  const [q, setQ] = useState('');
  if (!isPlatformAdmin) return <PermissionDenied />;

  const list = state.applications.filter((a) => `${a.name} ${a.key} ${a.category}`.toLowerCase().includes(q.toLowerCase()));
  const remove = (a: App) => { setState((s) => ({ ...s, applications: s.applications.filter((x) => x.id !== a.id) })); toast.success(`${a.name} removed from catalog.`); setConfirm(null); };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1100, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Applications" sub="The catalog of product modules tenants can subscribe to."
        action={<PButton kind="accent" icon="plus" onPress={() => setEditor({ open: true, app: null })}>Add application</PButton>} />

      <View style={{ marginBottom: 16, maxWidth: 320 }}>
        <PTextInput leadingIcon="search" placeholder="Search applications…" value={q} onChangeText={setQ} />
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
        {list.map((a) => (
          <View key={a.id} style={{ width: 340, flexGrow: 1, borderWidth: 1, borderColor: c.line, borderRadius: R.r3, backgroundColor: c.card, padding: 18 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <AppIcon icon={a.icon} size={40} />
                <View>
                  <Text style={{ fontWeight: FW.semibold, fontSize: FS.lg, color: c.ink }}>{a.name}</Text>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: FS.xs, color: c.ink3 }}>{a.key} · v{a.version}</Text>
                </View>
              </View>
              <StatusBadge status={a.status} />
            </View>
            <Text style={{ color: c.ink2, fontSize: FS.sm, minHeight: 34, marginBottom: 12 }}>{a.desc}</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                <Tag tone="neutral">{a.category}</Tag>
                <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{a.tenants} tenants</Text>
              </View>
              <View style={{ flexDirection: 'row', gap: 4 }}>
                <PButton size="sm" kind="ghost" icon="edit" iconOnly onPress={() => setEditor({ open: true, app: a })} />
                <PButton size="sm" kind="ghost" icon="trash" iconOnly onPress={() => setConfirm(a)} />
              </View>
            </View>
          </View>
        ))}
        {list.length === 0 && (
          <View style={{ width: '100%' }}><PEmpty icon="search" title="No applications match your search">Try a different term.</PEmpty></View>
        )}
      </View>

      <ApplicationEditor open={editor.open} app={editor.app} onClose={() => setEditor({ open: false, app: null })} />
      <ConfirmModal open={!!confirm} onClose={() => setConfirm(null)} title={`Remove ${confirm?.name}?`} confirmLabel="Remove application"
        body={`${confirm?.name} will be removed from the catalog. ${confirm?.tenants || 0} tenants currently use it — they'll lose access at their next renewal. This can't be undone.`}
        onConfirm={() => confirm && remove(confirm)} />
    </ScrollView>
  );
};

export default AdminApplications;
